/**
 * Unified hook for media cropping functionality
 * Handles video, image, and object cropping, coordinate transformation, file upload, and VQL queries
 */
import { useCallback } from "react";
import { createAndUpdateVisualSimilarityFilter } from "../helpers/utility/vqlHelpers";
import {
  saveVideoFrameToFile,
  transformCropCoordinatesForVideo,
  getVideoContentDimensions,
  cropCoordinatesToBoundingBox,
} from "../helpers/utility/mediaCroppingUtils";
import { useQueryImageUpload } from "./useImageUpload";
import useVqlParams from "./useVqlParams";

interface MediaUploadParams {
  mediaType: "IMAGE" | "VIDEO" | "OBJECT";
  videoElement?: HTMLVideoElement;
  imageData?: any;
  cropCoordinates: any;
  datasetId: string;
}

interface MediaUploadResult {
  uploadId: string;
  boundingBox?: [number, number, number, number];
}

interface MediaUploadAndNavigateParams extends MediaUploadParams {
  dataPath?: string;
  negation?: boolean;
}

export const useMediaCropping = () => {
  const { addVqlQuery } = useVqlParams();
  const { uploadQueryImage } = useQueryImageUpload();

  const handleCroppedMediaUpload = useCallback(
    async ({
      mediaType,
      videoElement,
      imageData,
      cropCoordinates,
      datasetId,
    }: MediaUploadParams): Promise<MediaUploadResult> => {
      if (mediaType === "VIDEO") {
        if (!videoElement) {
          throw new Error("Video element is required for video processing");
        }

        // Video processing logic - use actual content dimensions, not CSS element size
        const { width, height } = getVideoContentDimensions(videoElement);

        // Convert to integers ONCE at the hook level (ONLY place with Math.round)
        // Handle optional crop coordinates - only process if they exist
        if (!cropCoordinates) {
          throw new Error("Crop coordinates are required for video processing");
        }

        const intCropCoordinates = {
          x: Math.round(cropCoordinates.x),
          y: Math.round(cropCoordinates.y),
          width: Math.round(cropCoordinates.width),
          height: Math.round(cropCoordinates.height),
        };

        // Transform coordinates from SVG space to video space
        const transformedCoordinates = transformCropCoordinatesForVideo({
          cropCoordinates: intCropCoordinates,
          videoElement,
          svgDimensions: { width, height },
        });

        // Capture video frame with transformed coordinates
        const result = await saveVideoFrameToFile(
          videoElement,
          transformedCoordinates,
        );

        // Upload captured frame to backend
        const uploadId = await uploadQueryImage(result.file, datasetId);

        // Calculate bounding box from scaled coordinates
        const boundingBox = cropCoordinatesToBoundingBox(
          result.scaledCoordinates,
        );

        return {
          uploadId,
          boundingBox,
        };
      } else {
        // Image/Object processing logic
        const imageId = imageData?.id || imageData?.media_id;

        // Convert to integers ONCE at the hook level (ONLY place with Math.round)
        // Handle optional crop coordinates - only process if they exist
        let intCropCoordinates;
        let boundingBox;

        if (cropCoordinates) {
          intCropCoordinates = {
            x: Math.round(cropCoordinates.x),
            y: Math.round(cropCoordinates.y),
            width: Math.round(cropCoordinates.width),
            height: Math.round(cropCoordinates.height),
          };
          boundingBox = cropCoordinatesToBoundingBox(intCropCoordinates);
        }

        // Upload image to backend and change op to "upload"
        if (imageData?.file) {
          // Already have File object
          const uploadId = await uploadQueryImage(imageData.file, datasetId);
          return { uploadId, boundingBox };
        } else if (
          imageData?.video_uri ||
          imageData?.media_uri ||
          imageData?.image_uri ||
          imageData?.uri
        ) {
          // Create File from URL
          const imageUrl =
            imageData.media_uri || imageData.image_uri || imageData.uri;
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], imageData?.file_name || "image.jpg", {
            type: blob.type || "image/jpeg",
          });
          const uploadId = await uploadQueryImage(file, datasetId);
          return { uploadId, boundingBox };
        } else {
          // Fallback to existing imageId
          return { uploadId: imageId, boundingBox };
        }
      }
    },
    [uploadQueryImage],
  );

  const handleVqlUpload = useCallback(
    (
      anchorMediaId: string,
      boundingBox?: [number, number, number, number],
      op?: "upload" | "media",
      dataPath?: string,
      negation?: boolean,
    ) => {
      const visualSimilarityQuery = createAndUpdateVisualSimilarityFilter(
        anchorMediaId,
        boundingBox?.map((v) => Math.round(v)) as
          | [number, number, number, number]
          | undefined,
        undefined,
        op,
        negation,
      );

      // Use the VQL hook's addVqlQuery method which handles duplicate checking
      // Pass dataPath as customPathname to handle navigation
      addVqlQuery(visualSimilarityQuery, dataPath);
    },
    [addVqlQuery],
  );

  const getSvgCropperSrc = useCallback(
    (
      mediaType: "IMAGE" | "VIDEO" | "OBJECT",
      videoElement?: HTMLVideoElement,
      containerElement?: HTMLDivElement,
      imageSrc?: string,
    ): string => {
      if (mediaType === "VIDEO" && videoElement) {
        // ALWAYS use actual video CONTENT dimensions for consistent coordinate transformation
        // This accounts for letterboxing and maintains proper aspect ratio
        const dimensions = getVideoContentDimensions(videoElement);
        return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${dimensions.width}" height="${dimensions.height}"></svg>`;
      }
      return imageSrc || "";
    },
    [],
  );

  const handleCroppedMediaUploadAndNavigate = useCallback(
    async ({
      mediaType,
      videoElement,
      imageData,
      cropCoordinates,
      datasetId,
      dataPath,
      negation,
    }: MediaUploadAndNavigateParams): Promise<MediaUploadResult> => {
      // Step 1: Upload the media and get coordinates
      const result = await handleCroppedMediaUpload({
        mediaType,
        videoElement,
        imageData,
        cropCoordinates,
        datasetId,
      });

      // Step 2: Determine operation type - now use "upload" for both images and videos
      const operation = "upload";

      // Step 3: Create VQL query and navigate
      handleVqlUpload(
        result.uploadId,
        result.boundingBox,
        operation,
        dataPath,
        negation,
      );

      return result;
    },
    [handleCroppedMediaUpload, handleVqlUpload],
  );

  return {
    handleCroppedMediaUpload,
    handleVqlUpload,
    handleCroppedMediaUploadAndNavigate,
    getSvgCropperSrc,
  };
};
